import Keycloak, { type KeycloakInstance, type KeycloakTokenParsed } from "keycloak-js"

type AuthConfig = {
  url: string
  realm: string
  clientId: string
  resolutionCenterUrl?: string
  redirectToKeycloak?: boolean
}

export const DEFAULT_RESOLUTION_CENTER_URL = "https://portal.100581.cloudfit.software/resolutioncenter"
export const SIGNING_OUT_QUERY_PARAM = "signingOut"

export type AuthenticatedUser = {
  name: string
  email: string
  username: string
  avatar?: string
}

let keycloakClient: KeycloakInstance | null = null
let initializationPromise: Promise<KeycloakInstance> | null = null
let authConfig: AuthConfig | null = null

function createClient(config: AuthConfig) {
  const client = new Keycloak(config)

  client.onTokenExpired = async () => {
    try {
      await client.updateToken(30)
    } catch {
      client.clearToken()
    }
  }

  return client
}

function isThirdPartyCookieTimeout(error: unknown) {
  return error instanceof Error
    && error.message.includes("Timeout when waiting for 3rd party check iframe message")
}

function shouldSkipSilentSso(config: AuthConfig) {
  return window.location.protocol === "https:"
    && config.url.startsWith("http://")
}

async function fetchAuthConfig(): Promise<AuthConfig> {
  if (authConfig) {
    return authConfig
  }

  const response = await fetch("/api/config")

  if (!response.ok) {
    const details = await response.text()
    throw new Error(`The app could not load Keycloak configuration from the API (${response.status}). ${details}`)
  }

  authConfig = await response.json() as AuthConfig
  return authConfig
}

function normalizeResolutionCenterUrl(config: AuthConfig | null) {
  const configuredUrl = config?.resolutionCenterUrl?.trim()

  return configuredUrl && configuredUrl.length > 0
    ? configuredUrl
    : DEFAULT_RESOLUTION_CENTER_URL
}

export async function getResolutionCenterUrl() {
  if (authConfig) {
    return normalizeResolutionCenterUrl(authConfig)
  }

  const config = await fetchAuthConfig()
  return normalizeResolutionCenterUrl(config)
}

export function shouldRedirectToKeycloakLogin() {
  return authConfig?.redirectToKeycloak === true
}

function tokenValue(token: KeycloakTokenParsed | undefined, key: string) {
  const value = token?.[key]
  return typeof value === "string" && value.length > 0 ? value : undefined
}

function buildUser(parsedToken: KeycloakTokenParsed | undefined): AuthenticatedUser {
  const username = tokenValue(parsedToken, "preferred_username") ?? "unknown"
  const givenName = tokenValue(parsedToken, "given_name")
  const familyName = tokenValue(parsedToken, "family_name")
  const profileName = [givenName, familyName].filter(Boolean).join(" ")
  const name = tokenValue(parsedToken, "name")
    ?? (profileName || username)
  const email = tokenValue(parsedToken, "email") ?? `${username}@cloudfit.local`

  return {
    name,
    email,
    username,
  }
}

export async function initializeKeycloak() {
  if (keycloakClient) {
    return keycloakClient
  }

  if (initializationPromise) {
    return initializationPromise
  }

  initializationPromise = (async () => {
    try {
      const config = await fetchAuthConfig()
      const client = createClient(config)

      if (shouldSkipSilentSso(config)) {
        await client.init({
          pkceMethod: "S256",
          checkLoginIframe: false,
        })

        keycloakClient = client
        return client
      }

      try {
        await client.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          pkceMethod: "S256",
          checkLoginIframe: false,
          silentCheckSsoFallback: true,
        })

        keycloakClient = client
        return client
      } catch (error) {
        if (!isThirdPartyCookieTimeout(error)) {
          throw error
        }

        const fallbackClient = createClient(config)

        await fallbackClient.init({
          pkceMethod: "S256",
          checkLoginIframe: false,
        })

        keycloakClient = fallbackClient
        return fallbackClient
      }
    } catch (error) {
      initializationPromise = null
      throw error
    }
  })()

  return initializationPromise
}

export async function getAccessToken() {
  const client = await initializeKeycloak()

  if (!client.authenticated) {
    return null
  }

  await client.updateToken(30)
  return client.token ?? null
}

export async function getAuthenticatedUser(keycloak?: KeycloakInstance) {
  const client = keycloak ?? await initializeKeycloak()

  if (!client.authenticated) {
    throw new Error("A Keycloak session is not available.")
  }

  return buildUser(client.tokenParsed)
}

export async function loginWithKeycloak(loginHint: string) {
  const client = await initializeKeycloak()
  const hint = loginHint.trim()

  await client.login({
    redirectUri: window.location.href,
    ...(hint ? { loginHint: hint } : {}),
  })
}

export async function logoutFromKeycloak({ includeSigningOutParam = false }: { includeSigningOutParam?: boolean } = {}) {
  const client = await initializeKeycloak()
  const redirectUrl = new URL(window.location.origin)

  if (includeSigningOutParam) {
    redirectUrl.searchParams.set(SIGNING_OUT_QUERY_PARAM, "1")
  }

  await client.logout({ redirectUri: redirectUrl.toString() })
}